// Core sudoku domain types. The engine carries flat 81-element arrays —
// `row * 9 + col` indexing keeps cache-friendly iteration and trivial
// serialization to localStorage / share URL.

export const BOARD_SIZE = 9;
export const BOX_SIZE = 3;
export const CELL_COUNT = BOARD_SIZE * BOARD_SIZE;
export const DIGITS: readonly Digit[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];

export type Digit = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type NonEmptyDigit = Exclude<Digit, 0>;

/** Flat 81-cell board. `0` means empty. */
export type Grid = Digit[];

export interface Cell {
  readonly row: number;
  readonly col: number;
  readonly index: number;
}

export type Difficulty = 'easy' | 'medium' | 'hard';

/** Generator output. Versioned so future generator changes can invalidate
 * shared/saved seeds cleanly. */
export interface Puzzle {
  readonly givens: Grid;
  readonly solution: Grid;
  readonly seed: number;
  readonly difficulty: Difficulty;
  readonly generatorVersion: number;
}

export function cellAt(row: number, col: number): Cell {
  return { row, col, index: row * BOARD_SIZE + col };
}

export function indexToCell(index: number): Cell {
  const row = Math.floor(index / BOARD_SIZE);
  const col = index % BOARD_SIZE;
  return { row, col, index };
}

export function boxIndexOf(row: number, col: number): number {
  return Math.floor(row / BOX_SIZE) * BOX_SIZE + Math.floor(col / BOX_SIZE);
}

/** Empty 81-cell grid. */
export function emptyGrid(): Grid {
  return new Array(CELL_COUNT).fill(0) as Grid;
}

/** Shallow-copy a grid (cells are primitives, so this is a full clone). */
export function cloneGrid(grid: Grid): Grid {
  return grid.slice() as Grid;
}

export function isDigit(value: number): value is NonEmptyDigit {
  return Number.isInteger(value) && value >= 1 && value <= 9;
}

/** Indices of every cell sharing the same row, column, or 3×3 box as the
 *  given cell — peers used by classical sudoku constraints. The source cell
 *  itself is excluded. Pure; deterministic order: row sweep, then column,
 *  then box (de-duplicated). */
export function peerIndices(index: number): readonly number[] {
  const row = Math.floor(index / BOARD_SIZE);
  const col = index % BOARD_SIZE;
  const boxRow = Math.floor(row / BOX_SIZE) * BOX_SIZE;
  const boxCol = Math.floor(col / BOX_SIZE) * BOX_SIZE;
  const seen = new Set<number>();
  for (let c = 0; c < BOARD_SIZE; c += 1) seen.add(row * BOARD_SIZE + c);
  for (let r = 0; r < BOARD_SIZE; r += 1) seen.add(r * BOARD_SIZE + col);
  for (let r = boxRow; r < boxRow + BOX_SIZE; r += 1) {
    for (let c = boxCol; c < boxCol + BOX_SIZE; c += 1) {
      seen.add(r * BOARD_SIZE + c);
    }
  }
  seen.delete(index);
  return [...seen];
}
